import { StatisticalFeatures } from "../types";

// In-memory cache for reference-perplexity per text sample
const perplexityCache = new Map<string, number>();

// High-performance LRU-bounded caches for stylometric computation
const MAX_FEATURE_CACHE_ENTRIES = 250;
const featureExtractionCache = new Map<string, StatisticalFeatures>();
const syllableMemo = new Map<string, number>();
const featureZScoreCache = new Map<string, number[]>();

/**
 * Splits text into sentences using sentence-boundary punctuation and preserves delimiter spacing.
 */
export function splitIntoSentences(text: string): string[] {
  if (!text) return [];
  return text
    .split(/([.!?]+(?:\s+|\n+|$))/)
    .reduce((acc: string[], val, idx) => {
      if (idx % 2 === 0) {
        if (val.trim()) acc.push(val.trim());
      } else {
        if (acc.length > 0) acc[acc.length - 1] += val;
      }
      return acc;
    }, []);
}

// Helper: Calculate Syllables in a Word (with memoization to eliminate redundant regex evaluation)
function countSyllables(word: string): number {
  word = word.toLowerCase().replace(/[^a-z]/g, "");
  if (!word) return 1;
  const cached = syllableMemo.get(word);
  if (cached !== undefined) return cached;

  if (word.length <= 3) {
    if (syllableMemo.size < 4000) syllableMemo.set(word, 1);
    return 1;
  }
  const clean = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "").replace(/^y/, "");
  const matches = clean.match(/[aeiouy]{1,2}/g);
  const count = matches ? matches.length : 1;
  if (syllableMemo.size < 4000) {
    syllableMemo.set(word, count);
  }
  return count;
}

// Internal raw feature extractor
function extractFeaturesInternal(text: string): StatisticalFeatures {
  const sentences = text
    .split(/[.!?]+(?:\s+|\n+|$)/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const words = text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .split(/[\s\n]+/)
    .filter((w) => w.length > 0);

  const totalWords = words.length;
  const totalSentences = sentences.length || 1;

  // 1. Average Sentence Length (ASL)
  const asl = totalWords / totalSentences;

  // 2. Vocabulary Diversity (VD = Unique Words / Total Words)
  const uniqueWords = new Set(words);
  const totalUnique = uniqueWords.size;
  const vd = totalWords > 0 ? totalUnique / totalWords : 0;

  // 3. Herdan's C Lexical Richness Index (Herdan's C = log(V) / log(N))
  const herdanC = totalWords > 1 && totalUnique > 0 ? Math.log(totalUnique) / Math.log(totalWords) : 1;

  // 4. Repetition Score (RS = Sum of frequencies of repeated words / Total Words)
  const wordFreqs: Record<string, number> = {};
  for (const w of words) {
    wordFreqs[w] = (wordFreqs[w] || 0) + 1;
  }
  let repeatedWordsCount = 0;
  for (const w in wordFreqs) {
    if (wordFreqs[w] > 1) {
      repeatedWordsCount += wordFreqs[w];
    }
  }
  const rs = totalWords > 0 ? repeatedWordsCount / totalWords : 0;

  // 5. Burstiness & Coefficient of Variation
  const sentenceWordCounts = sentences.map((s) => {
    return s.split(/[\s\n]+/).filter((w) => w.length > 0).length;
  });
  const avgSentenceLength = sentenceWordCounts.reduce((a, b) => a + b, 0) / totalSentences;
  const variance = sentenceWordCounts.reduce((sum, len) => sum + Math.pow(len - avgSentenceLength, 2), 0) / totalSentences;
  const burstiness = Math.sqrt(variance); // Standard deviation
  const coefficientOfVariation = avgSentenceLength > 0 ? burstiness / avgSentenceLength : 0;

  // 6. Markov N-Gram Predictability & Synthetic Transition Cliché Density
  const markovBigrams: Record<string, number> = {};
  for (let i = 0; i < words.length - 1; i++) {
    const bg = `${words[i]} ${words[i + 1]}`;
    markovBigrams[bg] = (markovBigrams[bg] || 0) + 1;
  }
  let transitionEntropy = 0;
  const totalTransitions = words.length - 1;
  if (totalTransitions > 0) {
    for (const bg in markovBigrams) {
      const p = markovBigrams[bg] / totalTransitions;
      transitionEntropy -= p * Math.log2(p);
    }
  }
  const maxTransitionEntropy = totalTransitions > 1 ? Math.log2(totalTransitions) : 1;
  const relativeTransitionEntropy = totalTransitions > 0 ? transitionEntropy / maxTransitionEntropy : 0;
  const rawBigramRepeatPct = totalWords > 5 ? (1 - relativeTransitionEntropy) * 100 : 0;

  // Detect characteristic LLM discourse markers, transition clichés, and synthetic scaffolding
  const lowerClean = text.toLowerCase();
  const aiPhrasePatterns = [
    "in today's fast-paced",
    "interconnected digital era",
    "it is crucial to",
    "it is important to note",
    "it is important to remember",
    "it is imperative to",
    "it is essential to",
    "plays an indispensable role",
    "groundbreaking paradigm",
    "paradigm shift",
    "streamlining workflows",
    "driving sustainable innovation",
    "fostering cross-functional",
    "fostering proactive",
    "unlock unprecedented",
    "pave the way for",
    "in conclusion",
    "in summary",
    "furthermore,",
    "moreover,",
    "consequently,",
    "additionally,",
    "ultimately, the integration",
    "cornerstone of",
    "holistic framework",
    "holistic pedagogical",
    "seamless integration",
    "seamlessly",
    "tapestry of",
    "testament to",
    "delve into",
    "at the forefront of",
    "leverage proactive",
    "stakeholder engagement",
    "cross-functional collaboration",
    "cross-functional synergies",
    "sustainable competitive advantage",
    "mitigate unforeseen",
    "wide array of interconnected"
  ];
  let clicheHits = 0;
  for (const phrase of aiPhrasePatterns) {
    if (lowerClean.includes(phrase)) {
      clicheHits++;
    }
  }
  const clicheDensityBoost = totalWords > 0 ? Math.min(75, (clicheHits / Math.max(25, totalWords)) * 950) : 0;
  const predictabilityScore = totalWords > 5
    ? parseFloat(Math.min(95, rawBigramRepeatPct + clicheDensityBoost).toFixed(2))
    : 0;

  // 7. Shannon Entropy of Words (Information Entropy)
  let shannonEntropy = 0;
  for (const w in wordFreqs) {
    const p = wordFreqs[w] / totalWords;
    shannonEntropy -= p * Math.log2(p);
  }
  const maxPossibleEntropy = totalUnique > 1 ? Math.log2(totalUnique) : 1;
  const relativeEntropy = totalUnique > 0 ? shannonEntropy / maxPossibleEntropy : 0;

  // 8. Zipf's Law Fit
  const sortedFreqs = Object.values(wordFreqs).sort((a, b) => b - a);
  let zipfAlpha = 1.0;
  let zipfR2 = 1.0;

  if (sortedFreqs.length > 3) {
    const points = sortedFreqs.map((freq, index) => ({
      x: Math.log(index + 1),
      y: Math.log(freq)
    }));

    const kPoints = points.length;
    const sumX = points.reduce((acc, p) => acc + p.x, 0);
    const sumY = points.reduce((acc, p) => acc + p.y, 0);
    const avgX = sumX / kPoints;
    const avgY = sumY / kPoints;

    let num = 0;
    let den = 0;
    for (const p of points) {
      num += (p.x - avgX) * (p.y - avgY);
      den += (p.x - avgX) * (p.x - avgX);
    }

    const slope = den !== 0 ? num / den : -1;
    const intercept = avgY - slope * avgX;
    zipfAlpha = Math.abs(slope);

    let ssTot = 0;
    let ssRes = 0;
    for (const p of points) {
      const predY = slope * p.x + intercept;
      ssTot += Math.pow(p.y - avgY, 2);
      ssRes += Math.pow(p.y - predY, 2);
    }
    zipfR2 = ssTot !== 0 ? 1 - (ssRes / ssTot) : 1.0;
    zipfR2 = Math.max(0.1, Math.min(1.0, zipfR2));
  } else {
    zipfAlpha = 1.0;
    zipfR2 = 0.90;
  }

  // 9. Readability Metrics & Syllables
  let totalSyllables = 0;
  let complexWordsCount = 0;
  for (const w of words) {
    const sCount = countSyllables(w);
    totalSyllables += sCount;
    if (sCount >= 3) {
      complexWordsCount++;
    }
  }

  const fleschReadability = totalWords > 0 
    ? 206.835 - (1.015 * asl) - (84.6 * (totalSyllables / totalWords))
    : 100;

  const percentComplex = totalWords > 0 ? (complexWordsCount / totalWords) * 100 : 0;
  const gunningFog = 0.4 * (asl + percentComplex);

  // 10. Stylistic Punctuation Diversity Index & Punctuation Ratio
  const punctuationRegex = /[,;:!?\-()\[\]"']/g;
  const pMatches = text.match(punctuationRegex);
  const punctuationCount = pMatches ? pMatches.length : 0;
  const punctuationRatio = totalWords > 0 ? parseFloat((punctuationCount / totalWords).toFixed(4)) : 0;

  let punctuationEntropy = 0;
  if (pMatches && pMatches.length > 0) {
    const pFreqs: Record<string, number> = {};
    for (const char of pMatches) {
      pFreqs[char] = (pFreqs[char] || 0) + 1;
    }
    for (const char in pFreqs) {
      const p = pFreqs[char] / pMatches.length;
      punctuationEntropy -= p * Math.log2(p);
    }
  }

  // Average characters per word (RAID feature)
  const totalWordChars = words.reduce((acc, w) => acc + w.length, 0);
  const avgWordLength = totalWords > 0 ? parseFloat((totalWordChars / totalWords).toFixed(2)) : 0;

  // 11. Reference LM Log-Perplexity & Average Token Cross-Entropy
  let logPerplexity: number;
  if (perplexityCache.has(text)) {
    logPerplexity = perplexityCache.get(text)!;
  } else {
    const commonBigrams: Record<string, number> = {};
    for (let i = 0; i < words.length - 1; i++) {
      const bg = `${words[i]} ${words[i + 1]}`;
      commonBigrams[bg] = (commonBigrams[bg] || 0) + 1;
    }
    let totalBigramLogprob = 0;
    for (let i = 0; i < words.length - 1; i++) {
      const bg = `${words[i]} ${words[i + 1]}`;
      const unigramProb = (wordFreqs[words[i]] || 1) / totalWords;
      const condProb = (commonBigrams[bg] || 0.5) / (wordFreqs[words[i]] || 1);
      const interpolatedProb = 0.6 * condProb + 0.4 * unigramProb;
      totalBigramLogprob += -Math.log(Math.max(1e-5, Math.min(1.0, interpolatedProb)));
    }
    const avgNll = words.length > 1 ? totalBigramLogprob / (words.length - 1) : 3.5;
    const clichePenalty = Math.min(1.8, (predictabilityScore / 100) * 2.2);
    logPerplexity = parseFloat(Math.max(1.5, Math.min(6.5, avgNll + (1 - relativeEntropy) * 1.8 - clichePenalty)).toFixed(3));
    perplexityCache.set(text, logPerplexity);
  }
  const referenceLmPerplexity = parseFloat(Math.exp(logPerplexity).toFixed(2));

  return {
    asl: parseFloat(asl.toFixed(2)),
    avgSentenceLength: parseFloat(asl.toFixed(2)),
    vd: parseFloat(vd.toFixed(3)),
    lexicalDiversity: parseFloat(vd.toFixed(3)),
    rs: parseFloat(rs.toFixed(3)),
    repetitionScore: parseFloat(rs.toFixed(3)),
    burstiness: parseFloat(burstiness.toFixed(2)),
    predictabilityScore: parseFloat(predictabilityScore.toFixed(2)),
    totalWords,
    totalSentences,
    totalUnique,
    shannonEntropy: parseFloat(shannonEntropy.toFixed(3)),
    entropy: parseFloat(shannonEntropy.toFixed(3)),
    relativeEntropy: parseFloat(relativeEntropy.toFixed(3)),
    coefficientOfVariation: parseFloat(coefficientOfVariation.toFixed(3)),
    zipfR2: parseFloat(zipfR2.toFixed(4)),
    zipfAlpha: parseFloat(zipfAlpha.toFixed(3)),
    punctuationRatio,
    avgWordLength,
    punctuationEntropy: parseFloat(punctuationEntropy.toFixed(3)),
    fleschReadability: parseFloat(fleschReadability.toFixed(1)),
    gunningFog: parseFloat(gunningFog.toFixed(1)),
    herdanC: parseFloat(herdanC.toFixed(3)),
    logPerplexity,
    referenceLmPerplexity
  };
}

/**
 * Extract Statistical & Scientific Features from Text (Memoized with Bounded Cache)
 */
export function extractFeatures(text: string): StatisticalFeatures {
  const cached = featureExtractionCache.get(text);
  if (cached) return cached;

  const result = extractFeaturesInternal(text);
  if (featureExtractionCache.size >= MAX_FEATURE_CACHE_ENTRIES) {
    const firstKey = featureExtractionCache.keys().next().value;
    if (firstKey !== undefined) featureExtractionCache.delete(firstKey);
  }
  featureExtractionCache.set(text, result);
  return result;
}

/**
 * Computes calibrated standardized Z-scores across all 7 stylometric dimensions.
 * Perfectly harmonized with the Deep Forensic Diagnostics thresholds:
 * negative Z-score indicates human leaning, positive indicates machine leaning.
 * Strictly bounded in [-2.5, +2.5] to prevent single-metric outlier leverage.
 */
export function computeFeatureZScores(features: StatisticalFeatures) {
  const totalSents = features.totalSentences || 1;
  const predScore = features.predictabilityScore ?? 0;
  const hasSyntheticCliches = predScore >= 10;

  // 1. Sentence Length Variety (Coefficient of Variation / Burstiness)
  // Apply Bessel small-sample adjustment when passage has <= 4 sentences
  const rawCv = features.coefficientOfVariation ?? 0.35;
  const cv = totalSents <= 4 && !hasSyntheticCliches ? rawCv * 1.32 : rawCv;
  let cvZ = 0;
  if (cv >= 0.30 && !hasSyntheticCliches) {
    cvZ = -1.0 * Math.min(2.5, ((cv - 0.30) / 0.16) + 0.35);
  } else if (cv >= 0.38) {
    cvZ = -1.0 * Math.min(2.5, ((cv - 0.38) / 0.18) + 0.25);
  } else {
    cvZ = 1.0 * Math.min(2.5, ((0.32 - cv) / 0.14) + (hasSyntheticCliches ? 0.65 : 0.25));
  }

  // 2. Word Frequency Pattern (Zipf's Law R^2)
  const r2 = features.zipfR2 ?? 0.88;
  let zipfZ = 0;
  if (r2 <= 0.94 && !hasSyntheticCliches) {
    zipfZ = -1.0 * Math.min(2.5, ((0.94 - r2) / 0.08) + 0.25);
  } else {
    zipfZ = 1.0 * Math.min(2.5, ((r2 - 0.94) / 0.035) + (hasSyntheticCliches ? 0.55 : 0.25));
  }

  // Average Sentence Length (ASL)
  const asl = features.asl ?? features.avgSentenceLength ?? 18;
  let aslZ = 0;
  if (asl >= 16 && asl <= 23 && (rawCv < 0.26 || hasSyntheticCliches)) {
    aslZ = 1.0 * Math.min(2.5, 0.4 + (1 - Math.abs(asl - 19.5) / 4) * 0.85);
  } else {
    aslZ = -1.0 * Math.min(2.5, 0.3 + (Math.abs(asl - 19.5) / 7));
  }

  // 3. Information Richness (Entropy)
  const relEnt = features.relativeEntropy ?? 0.88;
  const entropyVal = features.shannonEntropy ?? features.entropy ?? (relEnt * 5.0);
  let entropyZ = 0;
  if ((entropyVal >= 4.2 || relEnt >= 0.86) && predScore < 18) {
    const diff = Math.max(entropyVal - 4.2, (relEnt - 0.86) * 5.0);
    entropyZ = -1.0 * Math.min(2.5, (diff / 0.9) + 0.35);
  } else {
    const diff = Math.max(0.2, 4.2 - entropyVal, (predScore - 12) / 18);
    entropyZ = 1.0 * Math.min(2.5, (diff / 0.9) + 0.4);
  }

  // 4. Vocabulary Diversity (Herdan's C Lexical Richness / Lexical Diversity)
  const herdan = features.herdanC ?? 0.78;
  const vd = features.vd ?? features.lexicalDiversity ?? 0.60;
  let herdanZ = 0;
  if ((herdan >= 0.78 || vd >= 0.60) && predScore < 22) {
    herdanZ = -1.0 * Math.min(2.5, ((herdan - 0.78) / 0.12) + 0.35);
  } else {
    herdanZ = 1.0 * Math.min(2.5, Math.max(0.35, ((0.78 - herdan) / 0.12) + (predScore / 35)));
  }

  // 5. Predictable AI Phrases & Clichés (Markov transition regularity & discourse clichés)
  let predZ = 0;
  if (predScore <= 6.0) {
    predZ = -1.0 * Math.min(2.5, ((6.0 - predScore) / 4.5) + 0.3);
  } else {
    predZ = 1.0 * Math.min(2.5, ((predScore - 6.0) / 14.0) + 0.45);
  }

  // 6. Sentence Structure Variety (Repetition Score RS & Transition Formulaicity)
  const repS = features.rs ?? features.repetitionScore ?? 0.25;
  let rsZ = 0;
  if (repS <= 0.35 && predScore < 12) {
    rsZ = -1.0 * Math.min(2.5, ((0.35 - repS) / 0.18) + 0.3);
  } else {
    rsZ = 1.0 * Math.min(2.5, Math.max(0.35, ((repS - 0.30) / 0.16) + (predScore / 28)));
  }

  // 7. Word Surprisal (Log-Perplexity / Cross-Entropy)
  const logPpl = features.logPerplexity ?? 3.5;
  let pplZ = 0;
  if (logPpl >= 3.0 && predScore < 18) {
    pplZ = -1.0 * Math.min(2.5, ((logPpl - 3.0) / 0.9) + 0.25);
  } else {
    pplZ = 1.0 * Math.min(2.5, Math.max(0.35, ((3.2 - logPpl) / 0.7) + 0.3));
  }

  // 8. Punctuation Ratio
  const pRatio = features.punctuationRatio ?? 0.08;
  const pEnt = features.punctuationEntropy ?? 0.8;
  let puncZ = 0;
  if (pRatio >= 0.055 && pRatio <= 0.105 && pEnt < 1.15 && hasSyntheticCliches) {
    puncZ = 1.0 * Math.min(2.5, 0.45 + (1 - Math.abs(pRatio - 0.08) / 0.035) * 0.85);
  } else {
    puncZ = -1.0 * Math.min(2.5, 0.3 + (Math.abs(pRatio - 0.08) / 0.04));
  }

  // 9. Average Word Length (AI prose frequently packs polysyllabic latinate nominalizations >= 5.35 chars)
  const avgWl = features.avgWordLength ?? 4.8;
  let wordZ = 0;
  if (avgWl >= 5.35 || (avgWl >= 5.05 && hasSyntheticCliches)) {
    wordZ = 1.0 * Math.min(2.5, ((avgWl - 5.0) / 0.45) + 0.4);
  } else {
    wordZ = -1.0 * Math.min(2.5, ((5.35 - avgWl) / 0.45) + 0.25);
  }

  // Symmetric length scaling for brief passages (under 50 words)
  const totWords = features.totalWords ?? 60;
  if (totWords < 50) {
    const scaleFactor = Math.max(0.75, Math.sqrt(totWords / 50));
    cvZ *= scaleFactor;
    zipfZ *= scaleFactor;
    entropyZ *= scaleFactor;
    herdanZ *= scaleFactor;
    predZ *= scaleFactor;
    rsZ *= scaleFactor;
    pplZ *= scaleFactor;
    aslZ *= scaleFactor;
    puncZ *= scaleFactor;
    wordZ *= scaleFactor;
  }

  const burstinessZ = cvZ;
  const entZ = entropyZ;
  const lexZ = herdanZ;
  const repZ = rsZ;

  return {
    cvZ,
    zipfZ,
    entropyZ,
    herdanZ,
    predZ,
    rsZ,
    pplZ,
    burstinessZ,
    aslZ,
    lexZ,
    repZ,
    entZ,
    puncZ,
    wordZ
  };
}

export function computeZScoresForText(text: string): number[] {
  const cached = featureZScoreCache.get(text);
  if (cached) return cached;

  const features = extractFeatures(text);
  const z = computeFeatureZScores(features);
  const result = [z.burstinessZ, z.aslZ, z.lexZ, z.repZ, z.entZ, z.puncZ, z.wordZ];
  featureZScoreCache.set(text, result);
  return result;
}
